/**
 *
 * SignupParent
 *
 */
import React, { memo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// assets
import CustomIcons from "components/CustomIcons";

// antd component
import { Alert, Button, Form, message } from "antd";

// layouts
import Preloader from "components/Preloader";

// components
import QrScanner from "mobile/components/QrScanner";

// forms helpers
import CustomForm from "components/Forms/CustomForm";
import { initFormsByRole } from "utils/forms/forms.init";

// global user
import { useInitUser } from "engine/Contexts/User.context";

// utils
import api from "utils/api";
import { useAuthRole } from "utils/hooks";
import _local from "utils/localStorageUtils";

export function SignupParent({ roles, history }) {
  const { t, i18n } = useTranslation();
  const initRole = "parent";
  const initForm = useCallback((initRole) => initFormsByRole(initRole), [initRole, i18n?.language]);

  const [newParent, setNewParent] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showQR, setShowQR] = useState(true);
  const [spinner, setSpinner] = useState(false);
  const [student, setStudent] = useState(false);
  const [isRegistrationSuccess, setIsRegistrationSuccess] = useState(false);
  const [code, setCode] = useState("");

  // Form initialization for "general" tab
  const [customForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const localUser = _local.getLocalUser();
  const reInitUser = useInitUser();
  const isAuthRole = useAuthRole(roles);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (window.location.pathname.includes("/signupparent")) {
      if (!!isAuthRole) {
        history.push("/linkstudent");
      } else if (
        (!!localUser?.token && localUser?.role == "teacher") ||
        localUser?.role == "admin"
      ) {
        history.push("/404");
      } else {
        setNewParent(true);
      }
    } else {
      if (localUser?.role != "parent") {
        history.push("/signupparent");
      } else {
        setNewParent(false);
      }
    }

    hideTab();
    // Anything in here is fired on component unmount.
    setSpinner(false);
  }, []);

  const hideTab = async () => {
    if (!localUser) {
      [].forEach.call(document.querySelectorAll(".fixed-nav-mobile"), function(
        el,
      ) {
        el.style.visibility = "hidden";
      });
    }
  };

  const redirectionMessageNewParent = () => {
    messageApi
      .open({
        type: "loading",
        content: t("pages.creatingParentAccount"),
        duration: 5,
      })
      .then(() => {
        setIsRegistrationSuccess(true);
        reInitUser();
      });
  };

  const redirectionMessageLink = () => {
    messageApi
      .open({
        type: "loading",
        content: t("pages.addingCurrentStudent"),
        duration: 5,
      })
      .then(() => {
        message.success(t("pages.addingCurrentStudentMsg"), 5);
        reInitUser();
      })
      .then(() => history.push("/home"));
  };

  const handleResultNewParent = code => {
   // console.debug("handleResultNewParent", code);
    setCode(code);
    setSpinner(true);
    api.students
      .validateToken(code)
      .then(result => {
        if (!!result) {
          //console.debug("validateToken std payload", JSON.stringify(result))
          setStudent(result);
          setShowForm(true);
          setShowQR(false);
          setSpinner(false);
        } else {
          setStudent(false);
          setShowForm(false);
          setShowQR(true);
          setSpinner(false);
          message.error(t("errors.invalidQR"), 5);
        }
        return result;
      })
      .catch(err => {
        setStudent(false);
        setShowForm(false);
        setShowQR(true);
        setSpinner(false);
        message.error(t("errors.invalidQR"), 5);
        console.error("is not valid", err);
      });
  };

  const handleResultLink = code => {
    setCode(code);
    api.students
      .validateToken(code)
      .then(async result => {
      //  console.debug("validateToken std payload", JSON.stringify(result));
        setStudent(result);
        setShowQR(false);
        let payload = {
          newUserId: localUser?._id,
          studentId: result?._id,
          school: result?.school,
        };
        api.users
          .linkStudent(payload)
          .then(result => {
            if (result) {
              redirectionMessageLink(result);
            } else {
             // console.debug("error linkStudent");
              message.error(t("errors.problemArisen"), 5);
            }
          })
          .catch(err => {
           // console.debug("error linkStudent");
            message.error(err.message, 5);
          });
      })
      .catch(err => {
    //    console.debug("error validateToken");
        setStudent(false);
        setShowQR(true);
        message.error(t("errors.invalidQR"), 5);
      });
  };

  const onSubmitForm = async data => {
    if (data?.description) {
      let parentBody = data;
      if (!!student?._id) {
        parentBody = {
          ...parentBody,
          school: student?.school,
        };
      //  console.debug("signUp user payload", JSON.stringify(parentBody))
        api.auth.signUp(parentBody)
          .then(newUser => {
             //console.debug("signUp user payload result ", JSON.stringify(newUser))
            if (!!newUser?.code && newUser?.code != 200) {
              message.error(newUser?.message, 5);
            } else {
              let payload = {
                newUserId: newUser?._id || newUser?.user?._id,
                studentId: student?._id,
                school: student?.school,
              };
              const token = newUser?.token || newUser?.user?.token;

              api.setAuthorizationHeader(token, student?.school);

              api.users.linkStudent(payload)
                .then(result => {
                  if (result) {
                    redirectionMessageNewParent(result);
                  } else {
                    message.error(t("errors.problemArisen"), 5);
                  }
                })
                .catch(err => {
                  message.error(err.message, 5);
                });
            }
          })
          .catch(err => {
            console.error({ err });
            message.error(err.message, 5);
          });
      } else {
        message.error(t("errors.noActiveStudentErr"), 5);
      }
    }
  };
  // render function
  return (
    <>
      {spinner && <Preloader fullWidth />}
      {!spinner && (
        <>
          <div className="login-page signup-parent-page">
            {isRegistrationSuccess ? (
              <div className={"success-container"}>
                <Alert
                  message={t("notifications.createAccountSuccess")}
                  description={t("notifications.createAccountSuccessMsg")}
                  type="success"
                  closable
                />
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  className="back-login"
                  onClick={() => {
                    history.push("/login");
                  }}
                >
                  {t("pages.backToLogin")}
                </Button>
              </div>
            ) : (
              <>
                {!!newParent && (
                  <div className="login-form__column register-parent">
                    {!isAuthRole && (
                      <div className="login-form__header">
                        <CustomIcons className="logo-login" type="logo" />
                      </div>
                    )}
                    {showQR && (
                      <h4>
                        {t("pages.receivedUniqueQR")}
                      </h4>
                    )}
                    {contextHolder}
                    {showQR && (
                      <QrScanner
                        getResult={code => handleResultNewParent(code)}
                      />
                    )}
                    {!!showForm && !!student && (
                      <div className="student-info">
                        <h4>
                          {t("pages.createdParentAccFor")}
                          :{" "}
                          <b>
                            {student?.description?.firstname}{" "}
                            {student?.description?.lastname}
                          </b>
                        </h4>
                      </div>
                    )}
                    {showForm && (
                      <CustomForm
                        name={`users-general-form-new-parent`}
                        className="register-user-form"
                        method="new"
                        form={customForm}
                        dataSource={initForm(initRole)}
                        handleSubmit={data => onSubmitForm(data)}
                        // handleChange={onFormChange}
                        // handleValuesChange={onFormValuesChange}
                        labelCol={24}
                        wrapperCol={24}
                      />
                    )}
                    {!showForm && (
                      <Button
                        type="primary"
                        htmlType="cancel"
                        block
                        size="large"
                        className="back-login"
                        loading={false}
                        onClick={() => history.push("/login")}
                      >
                        {t("pages.backToLogin")}
                      </Button>
                    )}
                  </div>
                )}
                {!newParent && (
                  <div className="login-form__column register-parent">
                    {showQR && (
                      <h4>
                        {t("pages.receivedUniqueQR")}
                      </h4>
                    )}
                    {contextHolder}
                    {showQR && (
                      <QrScanner getResult={code => handleResultLink(code)} />
                    )}
                  </div>
                )}
              </>
            )}
            <svg
              className="waves"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 24 150 28"
              preserveAspectRatio="none"
              shapeRendering="auto"
            >
              <defs>
                <path
                  id="gentle-wave"
                  d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"
                />
              </defs>
              <g className="parallax">
                <use
                  href="#gentle-wave"
                  x="48"
                  y="0"
                  fill="rgba(255,255,255,0.7"
                />
                <use
                  href="#gentle-wave"
                  x="48"
                  y="3"
                  fill="rgba(255,255,255,0.5)"
                />
                <use
                  href="#gentle-wave"
                  x="48"
                  y="5"
                  fill="rgba(255,255,255,0.3)"
                />
                <use href="#gentle-wave" x="48" y="7" fill="#fff" />
              </g>
            </svg>
          </div>
        </>
      )}
    </>
  );
}

export default memo(SignupParent);
